import { useAtomValue } from '@effect/atom-react';
import type { BillingInterval } from '@listening/api-contract/schemas';
import { kvsSyncReader } from '@listening/frontend/atoms/kvs';
import { Schema } from 'effect';
import { Atom } from 'effect/reactivity';

export const BillingPeriod = Schema.Literals(['yearly', 'monthly']);

const storageKey = 'checkout-billing-period';
const encodePeriod = Schema.encodeSync(Schema.fromJsonString(BillingPeriod));
const listeners = new Set<(period: typeof BillingPeriod.Type) => void>();
let rememberedPeriod: typeof BillingPeriod.Type | undefined;
const readBillingPeriod = kvsSyncReader({
  key: storageKey,
  schema: BillingPeriod,
  defaultValue: (): typeof BillingPeriod.Type => 'yearly',
  storage: {
    getItem: (key) => sessionStorage.getItem(key),
    setItem: (key, value) => sessionStorage.setItem(key, value),
    removeItem: (key) => sessionStorage.removeItem(key),
  },
});

export function rememberBillingPeriod(period: typeof BillingPeriod.Type) {
  rememberedPeriod = period;
  let persisted = true;
  try {
    sessionStorage.setItem(storageKey, encodePeriod(period));
  } catch {
    persisted = false;
  }
  for (const listener of listeners) listener(period);
  return persisted;
}

const billingPeriodAtom = Atom.make((get) => {
  const update = (period: typeof BillingPeriod.Type) => get.setSelf(period);
  listeners.add(update);
  get.addFinalizer(() => listeners.delete(update));
  return rememberedPeriod ?? readBillingPeriod();
}).pipe(
  Atom.keepAlive,
  Atom.withServerValue((): typeof BillingPeriod.Type => 'yearly'),
);

export const useBillingPeriod = () => useAtomValue(billingPeriodAtom);

export const billingIntervalFor = (
  period: typeof BillingPeriod.Type,
): BillingInterval => (period === 'monthly' ? 'month' : 'year');
